import {
  type Dispatch,
  type MouseEvent,
  type SetStateAction,
  useCallback,
  useEffect,
} from 'react';
import { flushSync } from 'react-dom';

const PUBLIC_NAV_SCROLL_TARGETS: Record<string, string> = {
  features: 'features',
  'how-it-works': 'how-header',
  manifesto: 'manifesto-content',
  pricing: 'pricing-content',
  faq: 'faq-header',
};
const SECTION_SCROLL_SETTLE_DELAY_MS = 500;
const SECTION_SCROLL_ASSIST_DURATION_MS = 300;
const SECTION_SCROLL_ASSIST_MAX_DISTANCE_PX = 120;
const SECTION_SCROLL_ASSIST_FAST_WINDOW_MS = 180;
const SECTION_SCROLL_ASSIST_FAST_WINDOW_DELTA = 480;
const SECTION_SCROLL_ASSIST_MEDIA_QUERY = '(min-width: 900px) and (pointer: fine)';

interface UseLandingSectionNavigationOptions {
  setIsMobileMenuOpen: Dispatch<SetStateAction<boolean>>;
  setVisibleSections: Dispatch<SetStateAction<Set<string>>>;
  visibleSections: Set<string>;
}

function shouldRevealTargetBeforeScroll(target: HTMLElement, visibleSections: Set<string>) {
  return target.hasAttribute('data-animate') && !visibleSections.has(target.id);
}

function documentOffsetTop(element: HTMLElement) {
  let top = 0;
  let currentElement: HTMLElement | null = element;

  while (currentElement) {
    top += currentElement.offsetTop;
    currentElement = currentElement.offsetParent as HTMLElement | null;
  }

  return top;
}

function fixedHeaderOffset(header: HTMLElement | null) {
  if (!header) return 20;

  const headerBounds = header.getBoundingClientRect();
  return headerBounds.top + headerBounds.height + 20;
}

function landingSections(): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>('.theme-public-shell > main > section'));
}

function sectionAtViewportCenter(sections: HTMLElement[]): number {
  const viewportCenter = window.scrollY + (window.innerHeight / 2);
  const containingSectionIndex = sections.findIndex((section) => {
    const sectionTop = documentOffsetTop(section);
    return viewportCenter >= sectionTop && viewportCenter < sectionTop + section.offsetHeight;
  });

  if (containingSectionIndex >= 0) return containingSectionIndex;

  return sections.reduce((closestIndex, section, index) => {
    const distance = Math.abs(documentOffsetTop(section) - viewportCenter);
    const closestDistance = Math.abs(documentOffsetTop(sections[closestIndex]) - viewportCenter);
    return distance < closestDistance ? index : closestIndex;
  }, 0);
}

function sectionScrollTarget(section: HTMLElement): HTMLElement {
  return section.querySelector<HTMLElement>('[data-landing-scroll-target]') ?? section;
}

export function useLandingSectionNavigation({
  setIsMobileMenuOpen,
  setVisibleSections,
  visibleSections,
}: UseLandingSectionNavigationOptions) {
  useEffect(() => {
    const sections = landingSections();
    if (sections.length < 2) return undefined;

    let settleTimeoutId: number | null = null;
    let assistTimeoutId: number | null = null;
    let isAssistedScrollRunning = false;
    let isScrollAssistArmed = false;
    let isFastScrollGesture = false;
    let lastAssistedSectionIndex: number | null = null;
    let lastWheelAt = 0;
    let wheelWindowStartedAt = 0;
    let wheelWindowDelta = 0;

    const armScrollAssist = (event: WheelEvent) => {
      const now = Date.now();
      const isNewGesture = now - lastWheelAt > SECTION_SCROLL_ASSIST_FAST_WINDOW_MS;
      const isNewSpeedWindow = now - wheelWindowStartedAt > SECTION_SCROLL_ASSIST_FAST_WINDOW_MS;

      if (isNewGesture) {
        isFastScrollGesture = false;
      }

      if (isNewGesture || isNewSpeedWindow) {
        wheelWindowStartedAt = now;
        wheelWindowDelta = 0;
      }

      lastWheelAt = now;
      wheelWindowDelta += Math.abs(event.deltaY);

      if (wheelWindowDelta > SECTION_SCROLL_ASSIST_FAST_WINDOW_DELTA) {
        isFastScrollGesture = true;
      }

      isScrollAssistArmed = (
        window.matchMedia(SECTION_SCROLL_ASSIST_MEDIA_QUERY).matches
        && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
        && !isFastScrollGesture
      );
    };

    const settleOnSectionTitle = () => {
      if (isAssistedScrollRunning) return;

      if (settleTimeoutId !== null) window.clearTimeout(settleTimeoutId);
      settleTimeoutId = window.setTimeout(() => {
        const nextSectionIndex = sectionAtViewportCenter(sections);

        if (
          lastAssistedSectionIndex !== null
          && nextSectionIndex !== lastAssistedSectionIndex
        ) {
          lastAssistedSectionIndex = null;
        }

        if (!isScrollAssistArmed) {
          return;
        }

        const footerBounds = document.querySelector('footer')?.getBoundingClientRect();
        if (footerBounds && footerBounds.top < window.innerHeight && footerBounds.bottom > 0) {
          isScrollAssistArmed = false;
          return;
        }

        isScrollAssistArmed = false;
        const target = sectionScrollTarget(sections[nextSectionIndex]);
        const header = document.getElementById('public-navigation');
        const targetScrollTop = Math.max(documentOffsetTop(target) - fixedHeaderOffset(header), 0);
        const scrollDistance = targetScrollTop - window.scrollY;

        if (
          Math.abs(scrollDistance) > SECTION_SCROLL_ASSIST_MAX_DISTANCE_PX
          || lastAssistedSectionIndex === nextSectionIndex
        ) {
          return;
        }

        lastAssistedSectionIndex = nextSectionIndex;
        isAssistedScrollRunning = true;

        window.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth',
        });

        assistTimeoutId = window.setTimeout(() => {
          isAssistedScrollRunning = false;
        }, SECTION_SCROLL_ASSIST_DURATION_MS);
      }, SECTION_SCROLL_SETTLE_DELAY_MS);
    };

    window.addEventListener('wheel', armScrollAssist, { passive: true });
    window.addEventListener('scroll', settleOnSectionTitle, { passive: true });

    return () => {
      window.removeEventListener('wheel', armScrollAssist);
      window.removeEventListener('scroll', settleOnSectionTitle);
      if (settleTimeoutId !== null) window.clearTimeout(settleTimeoutId);
      if (assistTimeoutId !== null) window.clearTimeout(assistTimeoutId);
    };
  }, []);

  const scrollToSection = useCallback((id: string) => {
    const targetId = PUBLIC_NAV_SCROLL_TARGETS[id] ?? id;
    const target = document.getElementById(targetId);
    const header = document.getElementById('public-navigation');

    if (!target) return;

    if (shouldRevealTargetBeforeScroll(target, visibleSections)) {
      flushSync(() => {
        setVisibleSections((currentSections) => new Set(currentSections).add(target.id));
      });
    }

    window.scrollTo({
      top: Math.max(documentOffsetTop(target) - fixedHeaderOffset(header), 0),
      behavior: 'smooth',
    });
  }, [setVisibleSections, visibleSections]);

  const scrollToSectionFromLink = useCallback((event: MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    scrollToSection(id);
  }, [scrollToSection]);

  const scrollToSectionFromMobileMenu = useCallback((id: string) => {
    flushSync(() => setIsMobileMenuOpen(false));
    scrollToSection(id);
  }, [scrollToSection, setIsMobileMenuOpen]);

  return {
    scrollToSection,
    scrollToSectionFromLink,
    scrollToSectionFromMobileMenu,
  };
}
